import {
  Box,
  capitalize,
  Grid2,
  Typography,
  useMediaQuery
} from '@mui/material'
import { withObservables } from '@nozbe/watermelondb/react'
import { CalendarBackIcon, CaretDownIcon, GroupChannelIcon } from 'assets/icons'
import ContainerWithBottomBadgeIcon from 'components/containers/ContainerWithBottomBadgeIcon'
import GroupPopup from 'components/popups/groupInfo/GroupPopup'
import GroupTitle from 'components/popups/groupInfo/GroupTitle'
import { useCalendar } from 'components/providers/CalendarProvider'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import ObserveService from 'services/db/Observe'
import { isUserLeft } from 'services/db/utils/GroupUtils'
import LocalStorage from 'services/LocalStorage'
import { AccountDoc, GroupDoc } from 'types/api/changes'
import { CalendarRange } from 'types/components/calendar'
import { Option } from 'types/components/form'
import { Model } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import { highestActiveTier } from 'utils/api/accounts'
import {
  defaultGroupImage,
  getActiveParticipants,
  groupsToImages,
  isGroupChannel,
  isGroupOutOfTier,
  isUserActiveInGroup
} from 'utils/api/groups'
import NamesStrip from './NamesStrip'
import * as Styled from './styled'
import { t } from 'i18next'
import GroupcalTooltip from 'components/tooltips/GroupcalTooltip'
import i18n from 'i18n'
import { SEARCH_MODE } from 'components/popups/searchItems/SearchPopup'
import SearchPopupButton from 'components/SearchPopupButton'
import { is24meApp } from 'utils/appType'
import { isGuest } from 'utils/user'
import { isMobileDevice } from 'utils/constants'
import { theme } from 'theme'
import Avatar from 'components/badges/Avatar'
import ViewDatesPicker from './ViewDatesPicker'
import { DateRange } from 'react-day-picker'
import Logger from 'services/Logger'
import usePersistState from 'hooks/usePersistState'

export const RANGE: Option[] = [
  { label: capitalize(t('day_one')), value: CalendarRange.DAY },
  { label: capitalize(t('week_one')), value: CalendarRange.WEEK },
  { label: capitalize(t('month_one')), value: CalendarRange.MONTH },
  { label: capitalize(t('list')), value: CalendarRange.LISTWEEK },
  { label: capitalize(t('dayList')), value: CalendarRange.LISTDAY }
]

export const dateFormat = 'YYYY-MM-DD'

interface CalendarHeaderProps {
  account?: Model<AccountDoc>
  groups?: Model<GroupDoc>[]
  currentGroup?: Model<GroupDoc>
  groupID: string
  currentView?: CalendarRange
  title?: string
  embedMode?: boolean
  dateRangeSelected?: (selected: Date | DateRange | undefined) => void
}

function CalendarHeader(props: CalendarHeaderProps) {
  const calendar = useCalendar()
  const [groupPoupOpen, setGroupPopupOpen] = useState(false)

  const isHeightBelowCondesed = useMediaQuery('(max-height: 480px)')
  const isMdUp = useMediaQuery(theme.breakpoints.up('lg'))

  useEffect(() => {
    if (calendar.groupInfo) showGroup()
  }, [calendar.groupInfo])

  const isUserNotActive =
    props.currentGroup &&
    !isUserActiveInGroup(
      props.currentGroup,
      LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
    )

  const showGroup = () => {
    if (isGuest()) return
    setGroupPopupOpen(true)
  }

  const hideGroup = () => {
    setGroupPopupOpen(false)
  }

  const readOnly = props.embedMode

  function separator() {
    return (
      <Box
        width={'1px'}
        height={'30px'}
        margin={2}
        sx={{ backgroundColor: theme.palette.border.primary }}
      />
    )
  }

  function nextPrevButtons() {
    const visibleAreaStart = dayjs(
      LocalStorage.get(LocalStorageKey.VISIBLE_FROM)
    )
    const visibleAreaEnd = dayjs(LocalStorage.get(LocalStorageKey.VISIBLE_TO))

    const startMonth = visibleAreaStart.format('MMM')
    const startDay = visibleAreaStart.format('DD')
    const startYear = visibleAreaStart.format('YYYY')

    const endMonth = visibleAreaEnd.format('MMM')
    const endDay = visibleAreaEnd.format('DD')
    const endYear = visibleAreaEnd.format('YYYY')

    let dateRangeString = ''

    if (startMonth === endMonth && startYear === endYear) {
      // Same month and same year
      dateRangeString = `${startMonth} ${startDay} - ${endDay}, ${endYear}`
    } else if (startYear === endYear) {
      // Different months but same year
      dateRangeString = `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${endYear}`
    } else {
      // Different months and different years
      dateRangeString = `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${endYear}`
    }
    return (
      <Grid2
        dir="ltr"
        display="flex"
        flexDirection={'row'}
        alignItems={'center'}
        whiteSpace={'nowrap'}
      >
        <Styled.IconButtonSlide
          data-prev={1}
          size="sm"
          onClick={i18n.dir() === 'ltr' ? calendar.prev : calendar.next}
        >
          <CaretDownIcon />
        </Styled.IconButtonSlide>
        {isMdUp && calendar.view === CalendarRange.LISTWEEK ? (
          <Box
            sx={{
              flexGrow: 1,
              textAlign: 'center',
              fontSize: '1rem',
              color: theme.palette.text.primary,
              '@media (min-height: 481px)': {
                fontSize: '1.25rem !important'
              }
            }}
          >
            {dateRangeString}
          </Box>
        ) : (
          <></>
        )}
        <Styled.IconButtonSlide
          data-next={1}
          size="sm"
          onClick={i18n.dir() === 'ltr' ? calendar.next : calendar.prev}
        >
          <CaretDownIcon />
        </Styled.IconButtonSlide>
      </Grid2>
    )
  }

  return (
    <Styled.CalendarHeader
      style={{ cursor: 'pointer' }}
      className="calendar-header"
    >
      {props.currentGroup?._id ? (
        <GroupPopup
          setOpen={setGroupPopupOpen}
          isOpen={groupPoupOpen}
          groupID={props.currentGroup._id}
          group={props.currentGroup}
        >
          <Styled.CalendarHeaderInfo
            id="calendar-info-calendar"
            onClick={showGroup}
          >
            <ContainerWithBottomBadgeIcon
              showLock={isGroupOutOfTier(
                highestActiveTier(props.account),
                props.currentGroup,
                props.account
              )}
              topRightIcon={
                isGroupChannel(props.currentGroup) ? (
                  <GroupChannelIcon width={18} height={18} />
                ) : undefined
              }
              marginRight={6}
            >
              <Avatar
                name=""
                size="sm"
                asGroup
                img={props.currentGroup.Photo}
                emptyPic={defaultGroupImage(props.currentGroup)}
                sx={{
                  '@media (max-height: 480px)': {
                    width: '26px !important',
                    height: '26px !important'
                  }
                }}
              />
            </ContainerWithBottomBadgeIcon>

            <Styled.CalendarHeaderTitleContainer className="calendar-info-wrapper">
              <GroupcalTooltip
                hidden={isGuest()}
                title={t('clickToSeeCalendarInfo')}
              >
                <Styled.CalendarHeaderTitle>
                  {props.title ? (
                    <Typography
                      fontSize={'1.5rem'}
                      sx={{
                        '@media (max-height: 480px)': {
                          fontSize: '16px'
                        }
                      }}
                    >
                      {props.title}
                    </Typography>
                  ) : (
                    <GroupTitle
                      group={props.currentGroup}
                      iconHeight={'20px'}
                    />
                  )}
                </Styled.CalendarHeaderTitle>
              </GroupcalTooltip>
              {readOnly || isHeightBelowCondesed ? (
                <></>
              ) : (
                <GroupcalTooltip
                  hidden={isGuest()}
                  title={t('clickToSeeCalendarInfo')}
                >
                  <Styled.CalendarHeaderSubtitle
                    color={isUserNotActive ? 'red' : undefined}
                  >
                    {isUserNotActive && !readOnly ? (
                      isUserLeft(
                        props.currentGroup,
                        LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
                      ) ? (
                        'You left the shared calendar'
                      ) : (
                        'You have been removed from the shared calendar'
                      )
                    ) : readOnly ? (
                      <></>
                    ) : (
                      <NamesStrip
                        group={props.currentGroup}
                        participantIds={Object.keys(
                          getActiveParticipants(props.currentGroup.Participants)
                        )}
                      />
                    )}
                  </Styled.CalendarHeaderSubtitle>
                </GroupcalTooltip>
              )}
            </Styled.CalendarHeaderTitleContainer>
          </Styled.CalendarHeaderInfo>
        </GroupPopup>
      ) : (
        <Styled.CalendarHeaderInfo>
          <Styled.AllAccountBadge
            groups={props.groups}
            images={groupsToImages(props.groups)}
          />
          <Styled.CalendarHeaderTitleContainer>
            <Styled.CalendarHeaderTitle
              sx={{ fontSize: isHeightBelowCondesed ? '16px' : '1.5rem' }}
            >
              {t('allCalendarsButton')}
            </Styled.CalendarHeaderTitle>
          </Styled.CalendarHeaderTitleContainer>
        </Styled.CalendarHeaderInfo>
      )}

      <Styled.CalendarHeaderActions>
        <NextPrevButtons dateRangeSelected={props.dateRangeSelected} />
        {/* <Styled.CalendarHeaderActionsCell>
          <Styled.RangeSelect
            variant="no-border"
            placeholder="Month"
            size="lg"
            className="select-month-wrapper"
            value={calendar.currentMonth}
            options={calendar.monthArray}
            onChange={(e) => {
              calendar.onCurrentMonth(`${e?.value}`)
              calendar.onGoToDate(dayjs(e?.value as string).toDate())
            }}
          />
          
        </Styled.CalendarHeaderActionsCell> */}
        {separator()}
        <Styled.CalendarHeaderActionsCell>
          <Styled.RangeSelect
            sx={{ direction: 'ltr' }}
            variant="no-border"
            id="calendar-view-select"
            placeholder="Range"
            className={`select-view ${calendar.view}`}
            size="lg"
            options={RANGE}
            value={calendar.view}
            onChange={(e) => {
              console.log(calendar.ref)
              calendar.onView(e?.value as CalendarRange)
            }}
          />
          {separator()}
        </Styled.CalendarHeaderActionsCell>
        <GroupcalTooltip title={t('backToToday')}>
          <Grid2
            id="go-today-button"
            className="today-button"
            onClick={calendar.onGoToToday}
            sx={{
              marginLeft:
                i18n.dir() === 'ltr'
                  ? isMobileDevice()
                    ? '6px'
                    : '8px'
                  : '16px',
              marginRight:
                i18n.dir() === 'rtl'
                  ? isMobileDevice()
                    ? '6px'
                    : '8px'
                  : isMobileDevice() || isGuest()
                    ? undefined
                    : '16px',
              [theme.breakpoints.down('lg')]: {
                marginRight: 0,
                marginLeft: 0
              },
              '@media (max-height: 480px)': {
                marginRight: 0,
                marginLeft: 0
              }
            }}
          >
            <span className="today-text">{t('today')}</span>
            <CalendarBackIcon className="today-image" />
          </Grid2>
        </GroupcalTooltip>

        {isGuest() || props.embedMode ? (
          <></>
        ) : (
          <SearchPopupButton
            groupId={props.currentGroup?._id}
            mode={SEARCH_MODE.OnlyEvents}
            textfieldPlaceholder={
              (calendar.groupServerID.length > 0
                ? t('searchItemsIn', {
                    what: t('events').toLowerCase(),
                    where: props.currentGroup?.Name ?? ''
                  })
                : is24meApp()
                  ? t('searchItems', {
                      what: t('eventsTasksNotes').toLowerCase()
                    })
                  : t('searchItems', { what: t('events').toLowerCase() })) ?? ''
            }
          />
        )}
      </Styled.CalendarHeaderActions>
    </Styled.CalendarHeader>
  )
}

interface NextPrevButtonsProps {
  dateRangeSelected?: (selected: Date | DateRange | undefined) => void
}

function NextPrevButtons(props: NextPrevButtonsProps) {
  const isMdUp = useMediaQuery(theme.breakpoints.up('lg'))
  const calendar = useCalendar()

  const currentView = calendar.view

  console.log(calendar.fromISO)

  const visibleAreaStart = dayjs(calendar.fromISO)
  const visibleAreaEnd = dayjs(calendar.toISO).subtract(1, 'day')

  const center = visibleAreaStart.add(2, 'week')

  const startMonth = visibleAreaStart.format('MMM')
  const startDay = visibleAreaStart.format('DD')
  const startYear = visibleAreaStart.format('YYYY')

  const endMonth = visibleAreaEnd.format('MMM')
  const endDay = visibleAreaEnd.format('DD')
  const endYear = visibleAreaEnd.format('YYYY')

  let dateRangeString = ''

  if (startMonth === endMonth && startYear === endYear) {
    // Same month and same year
    dateRangeString = `${startMonth} ${startDay} - ${endDay}, ${endYear}`
  } else if (startYear === endYear) {
    // Different months but same year
    dateRangeString = `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${endYear}`
  } else {
    // Different months and different years
    dateRangeString = `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${endYear}`
  }

  switch (currentView) {
    case CalendarRange.LISTDAY:
    case CalendarRange.DAY:
      dateRangeString = visibleAreaStart.format('MMM DD, YYYY')
      break
    case CalendarRange.MONTH:
      dateRangeString = center.format('MMM YYYY')
      break
  }
  return (
    <Grid2
      display="flex"
      flexDirection={'row'}
      alignItems={'center'}
      whiteSpace={'nowrap'}
    >
      <Styled.IconButtonSlide
        data-prev={i18n.dir() === 'ltr' ? 1 : undefined}
        data-next={i18n.dir() === 'rtl' ? 1 : undefined}
        size="sm"
        onClick={calendar.prev}
      >
        <CaretDownIcon />
      </Styled.IconButtonSlide>
      <Styled.IconButtonSlide
        data-next={i18n.dir() === 'ltr' ? 1 : undefined}
        data-prev={i18n.dir() === 'rtl' ? 1 : undefined}
        size="sm"
        onClick={calendar.next}
      >
        <CaretDownIcon />
      </Styled.IconButtonSlide>
      {isMdUp && (
        <Box
          display={'flex'}
          alignItems={'center'}
          justifyContent={'center'}
          alignContent={'center'}
          flexDirection={'row'}
          sx={{
            flexGrow: 1,
            textAlign: 'center',
            fontSize: '1rem',
            height: 52,
            borderRadius: 23,
            cursor: 'pointer',
            minWidth:
              calendar.view === CalendarRange.MONTH ||
              calendar.view === CalendarRange.DAY ||
              calendar.view === CalendarRange.LISTDAY
                ? '175px'
                : '250px',
            color: theme.palette.text.primary,
            '@media (min-height: 481px)': {
              fontSize: '1.25rem !important'
            },
            '@media (max-height: 480px)': {
              height: 32,
              minWidth:
                calendar.view === CalendarRange.MONTH ||
                calendar.view === CalendarRange.DAY ||
                calendar.view === CalendarRange.LISTDAY
                  ? '150px'
                  : '200px'
            },
            '&:hover': {
              // border: `1px solid ${theme.palette.input.border.primary}`,
              boxShadow: '0px 1px 6px 0px #32324714 inset',
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <ViewDatesPicker
            mode={
              currentView === CalendarRange.LISTWEEK ||
              currentView === CalendarRange.WEEK
                ? 'range'
                : 'single'
            }
            defaultRange={{
              from: visibleAreaStart.toDate(),
              to: visibleAreaEnd.toDate()
            }}
            defaultDate={
              calendar.view === CalendarRange.MONTH
                ? center.toDate()
                : visibleAreaStart.toDate()
            }
            onSelect={props.dateRangeSelected}
            withoutSelection={calendar.view === CalendarRange.MONTH}
          >
            <Grid2 display={'flex'} flexDirection={'row'} alignItems={'center'}>
              <div style={{ paddingLeft: 8, paddingRight: 8 }}>
                {dateRangeString}
              </div>
              <CaretDownIcon
                style={{
                  marginRight: i18n.dir() === 'ltr' ? 8 : 0,
                  marginLeft: i18n.dir() === 'rtl' ? 8 : 0
                }}
              />
            </Grid2>
          </ViewDatesPicker>
        </Box>
      )}
    </Grid2>
  )
}

export default withObservables(['groupID'], (props: CalendarHeaderProps) => ({
  account: ObserveService.Account(),
  groups: ObserveService.Groups(),
  currentGroup: ObserveService.GroupByServerID(props.groupID)
}))(CalendarHeader)
